import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import {
  MatCheckboxChange,
  MatDialog,
  MatDialogRef,
  MatPaginator,
  MatSnackBar,
  MatTableDataSource,
  MAT_DIALOG_DATA
} from '@angular/material';
import { TranslocoService } from '@ngneat/transloco';
import { capitalize, escape as escapeHtml } from 'lodash-es';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { v4 as uuid } from 'uuid';
import { ConsentSendService } from 'src/app/core/api/consent-send.service';
import { Patient } from 'src/app/core/api/patient.service';
import { QuestionnaireSendBuilderService } from 'src/app/core/api/questionnaire-send-builder.service';
import { StaffService } from 'src/app/core/api/staff.service';
import { AccessState, QuestionnaireDeliveryMethod } from 'src/app/core/schemas/subscription-plan.schema';
import { SubscriptionPlanAccessService } from 'src/app/core/services/subscription-plan-access.service';
import { MassClinicService } from 'src/app/mass-clinics/mass-clinic.service';
import {
  ImportWizardSendQuestionnairesService,
  PreparedQuestionnaireSend,
  SendQuestionnairesResult
} from 'src/app/patients/patient-import-wizard-modal/import-wizard-send-questionnaires/import-wizard-send-questionnaires.service';
import { ConfirmModalService } from '../confirm-modal/confirm-modal.service';
import { PatientFormControlType } from '../patient-form/patient-form.model';
import { QuestionnairePickerComponent } from '../questionnaire-picker/questionnaire-picker.component';
import {
  PickerEntry,
  QuestionnairePickerSelection
} from '../questionnaire-picker/questionnaire-picker.model';

export interface SendMultipleQuestionnairesDialogData {
  patients: Patient[];
}

/** Step 1 picks what to send, step 2 picks who receives it. */
export type SendMultipleQuestionnairesStep = 'select' | 'recipients';

/** One header in the stepper, with its state resolved off the change-detection path. */
export interface StepView {
  id: SendMultipleQuestionnairesStep;
  /** The number shown in the marker, replaced by a tick once the step is complete. */
  position: number;
  label: string;
  isActive: boolean;
  isComplete: boolean;
  /** Only a step the user can actually reach from here responds to a click. */
  isClickable: boolean;
}

/**
 * One row of the recipients table, with its warnings resolved off the change-detection path.
 *
 * The row owns its two channel checkboxes rather than being matched to them by position. The
 * table is paginated, so the index the template hands back is the index within the rendered page,
 * not within `recipientRows` — anything addressed by that index would bind page 2's first row to
 * the first patient's checkbox.
 */
export interface RecipientRow {
  patient: Patient;
  missingEmail: boolean;
  missingPhone: boolean;
  byEmail: FormControl;
  byPhone: FormControl;
}

/**
 * Holds every row's channel controls so the dialog has a single `valueChanges` to react to. These
 * are the same control instances the rows carry, so nothing here is read by index.
 */
class SendMethodsFormGroup extends FormGroup {
  controls: { byPhone: FormArray; byEmail: FormArray };

  constructor(fb: FormBuilder) {
    super({
      byPhone: fb.array([]),
      byEmail: fb.array([])
    });
  }

  insertAt(index: number, row: RecipientRow) {
    this.controls.byPhone.insert(index, row.byPhone);
    this.controls.byEmail.insert(index, row.byEmail);
  }

  removeAt(index: number) {
    this.controls.byPhone.removeAt(index);
    this.controls.byEmail.removeAt(index);
  }
}

@Component({
  selector: 'csi-send-multiple-questionnaires-dialog',
  templateUrl: './send-multiple-questionnaires-dialog.component.html',
  styleUrls: ['./send-multiple-questionnaires-dialog.component.scss']
})
export class SendMultipleQuestionnairesDialogComponent implements OnInit, OnDestroy {
  @Output() completed: EventEmitter<null> = new EventEmitter();
  @ViewChild(QuestionnairePickerComponent, { static: false })
  picker: QuestionnairePickerComponent;

  /**
   * The paginator lives behind step 2's *ngIf and behind a row-count *ngIf, so it arrives and
   * leaves during the dialog's life. A setter catches both: clearing `dataSource.paginator` on the
   * way out is what stops a destroyed paginator's stale page index from hiding rows.
   */
  @ViewChild(MatPaginator, { static: false })
  set recipientsPaginator(paginator: MatPaginator) {
    this.paginator = paginator;
    if (this.dataSource) {
      this.dataSource.paginator = paginator || null;
    }
  }
  private paginator: MatPaginator;

  public step: SendMultipleQuestionnairesStep = 'select';
  public steps: StepView[] = [];

  /** Recipients with at least one usable channel, newest first. */
  public recipientRows: RecipientRow[] = [];
  /** Feeds the table; the paginator only ever changes which of these rows are rendered. */
  public dataSource: MatTableDataSource<RecipientRow>;
  /** Rows per page before the paginator appears. */
  public readonly recipientsPageSize = 10;
  public readonly recipientsPageSizeOptions = [10, 25, 50];
  /** Recipients held back because they have neither an email nor a phone. */
  public patientsNeedingContactInformation: Patient[] = [];

  public readonly columnsToDisplay = [
    PatientFormControlType.FirstName,
    PatientFormControlType.LastName,
    PatientFormControlType.Email,
    PatientFormControlType.Phone,
    'sendToEmail',
    'sendToPhone',
    'remove'
  ];

  public sendMethods: SendMethodsFormGroup;
  public sendToPhoneIndeterminate = false;
  public sendToEmailIndeterminate = false;
  public readonly sendToPhoneControl = this.fb.control(false);
  public readonly sendToEmailControl = this.fb.control(true);
  public languageFormControl = new FormControl();

  /** Step 1 state, mirrored from the picker. */
  public questionnaireTypesFormGroup = new FormGroup({});
  public selectedItems: PickerEntry[] = [];
  public sendIntakeFormsEnabled = true;
  public canManageOneClickQRESets = false;
  public saveOneClickQRESetDisabled = true;
  /**
   * Consent form key -> why it cannot be sent from here. A consent form with clinicianOnly fields
   * needs its prefill answered per send, which has no meaning across a batch of patients.
   */
  public blockedConsentKeys: { [key: string]: string } = {};

  public alreadyExists = false;
  public sending = false;
  /** Precomputed for the template: recipients x selected questionnaires, skipping unreachable rows. */
  public requestCount = 0;
  /** Drives the Send button's label: how many patients will actually receive something. */
  public reachableRecipientCount = 0;
  /** Same bracketed-count label the single-patient dialog uses, over recipients rather than items. */
  public sendButtonLabel = '';
  /**
   * Names the unit behind the Send button's bracketed count. The summary pane alongside it counts
   * questionnaires and requestCount is the product of the two, so the number alone is ambiguous.
   */
  public sendButtonTooltip = '';
  public sendDisabled = true;
  public patientsNeedingContactLabel = '';

  public emailDeliveryEnabled: boolean;
  public phoneDeliveryEnabled: boolean;

  constructor(
    private fb: FormBuilder,
    private importWizardSendQuestionnairesService: ImportWizardSendQuestionnairesService,
    @Inject(MAT_DIALOG_DATA) public data: SendMultipleQuestionnairesDialogData,
    private dialogRef: MatDialogRef<SendMultipleQuestionnairesDialogComponent>,
    public staffService: StaffService,
    private massClinicService: MassClinicService,
    private snackBar: MatSnackBar,
    private confirmModalService: ConfirmModalService,
    private consentSendService: ConsentSendService,
    private questionnaireSendBuilderService: QuestionnaireSendBuilderService,
    private subscriptionPlanAccessService: SubscriptionPlanAccessService,
    public translocoService: TranslocoService
  ) {
    this.emailDeliveryEnabled = this.isDeliveryChannelEnabled(QuestionnaireDeliveryMethod.EMAIL);
    this.phoneDeliveryEnabled = this.isDeliveryChannelEnabled(QuestionnaireDeliveryMethod.TEXT);
  }

  public static open(matDialog: MatDialog, patients: Patient[] = []) {
    return matDialog.open<
      SendMultipleQuestionnairesDialogComponent,
      SendMultipleQuestionnairesDialogData
    >(SendMultipleQuestionnairesDialogComponent, {
      maxHeight: '90vh',
      // Wider than the single-patient dialog: step 2 carries a five-column recipients table.
      maxWidth: '95vw',
      width: '76rem',
      disableClose: true,
      data: { patients }
    });
  }

  /** Required by untilDestroyed(this), which hooks this method to complete its subscriptions. */
  ngOnDestroy() {}

  ngOnInit() {
    this.blockedConsentKeys = this.buildBlockedConsentKeys();
    this.languageFormControl.setValue(
      (this.staffService.staff && this.staffService.staff.preferredLanguage) ||
        this.translocoService.getDefaultLang()
    );

    // Default each row's phone checkbox off when the plan doesn't include TEXT delivery
    // (Essentials), so locked rows don't silently carry "true" values through to the send
    // payload. Email stays opt-out for everyone.
    this.sendMethods = new SendMethodsFormGroup(this.fb);
    this.sendToPhoneControl.setValue(this.phoneDeliveryEnabled);
    this.dataSource = new MatTableDataSource<RecipientRow>(this.recipientRows);

    // Pre-seeded patients are partitioned rather than all added: one with no contact information
    // would otherwise sit in the table looking like a recipient and silently receive nothing.
    // They keep the order they were selected in; only later additions jump to the top.
    (this.data.patients || []).forEach(patient => this.addPatient(patient, 'end'));

    this.sendMethods.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => this.refreshSendState());
    this.refreshSendState();
  }

  /** Keeps the Send button's label and enabled state off the change-detection hot path. */
  private refreshSendState() {
    this.reachableRecipientCount = this.recipientRows.filter(row => this.isReachable(row)).length;
    this.requestCount = this.reachableRecipientCount * this.selectedItems.length;
    this.sendDisabled = this.sending || this.requestCount === 0;
    this.sendButtonLabel = this.translocoService.translate('Send ({{count}})', {
      count: this.reachableRecipientCount
    });
    this.sendButtonTooltip = this.translocoService.translate('{{count}} patient(s)', {
      count: this.reachableRecipientCount
    });
    this.refreshSteps();
  }

  /** Rebuilds the stepper headers; the selection gates whether step 2 can be reached. */
  private refreshSteps() {
    const hasSelection = this.selectedItems.length > 0;
    const onRecipients = this.step === 'recipients';
    this.steps = [
      {
        id: 'select',
        position: 1,
        label: 'Select questionnaires',
        isActive: !onRecipients,
        isComplete: onRecipients,
        isClickable: onRecipients && !this.sending
      },
      {
        id: 'recipients',
        position: 2,
        label: 'Add patients',
        isActive: onRecipients,
        isComplete: false,
        isClickable: !onRecipients && hasSelection
      }
    ];
  }

  public onStepClick(stepView: StepView) {
    if (!stepView.isClickable) {
      return;
    }
    if (stepView.id === 'recipients') {
      this.goToRecipients();
      return;
    }
    this.goToSelection();
  }

  public trackByStepId(_index: number, stepView: StepView): string {
    return stepView.id;
  }

  private isReachable(row: RecipientRow): boolean {
    const channels = this.channelsFor(row);
    return channels.sendToEmail || channels.sendToPhone;
  }

  /**
   * Reading a control directly reports the value of a disabled control, which a FormArray's
   * `value` would have dropped — that is why nothing here goes through getRawValue().
   */
  private channelsFor(row: RecipientRow): { sendToEmail: boolean; sendToPhone: boolean } {
    return {
      sendToEmail: row.byEmail.value === true && !!row.patient.email,
      sendToPhone: row.byPhone.value === true && !!row.patient.phone
    };
  }

  /**
   * A row's warnings depend only on the patient and the plan, so they are resolved once. The row
   * also gets its own channel controls: a patient with no email cannot be emailed and one with no
   * phone cannot be texted, so that checkbox is cleared and disabled rather than left looking
   * actionable.
   */
  private toRecipientRow(patient: Patient): RecipientRow {
    // Phone defaults off when the plan doesn't include TEXT delivery (Essentials), so locked rows
    // don't silently carry "true" values through to the send payload.
    const byEmail = this.fb.control(!!patient.email);
    const byPhone = this.fb.control(this.phoneDeliveryEnabled && !!patient.phone);
    if (!patient.email) {
      byEmail.disable({ emitEvent: false });
    }
    if (!patient.phone) {
      byPhone.disable({ emitEvent: false });
    }
    return {
      patient,
      missingEmail: this.emailDeliveryEnabled && !patient.email,
      missingPhone: this.phoneDeliveryEnabled && !patient.phone,
      byEmail,
      byPhone
    };
  }

  /* --- Step 1: what to send ---------------------------------------------- */

  private buildBlockedConsentKeys(): { [key: string]: string } {
    const reason = this.translocoService.translate(
      'This consent form needs information from staff, so it can only be sent to one patient at a time.'
    );
    return this.consentSendService
      .getConsentFormKeysRequiringClinicianInput()
      .reduce((blockedKeys, key) => {
        blockedKeys[key] = reason;
        return blockedKeys;
      }, {});
  }

  public onPickerSelectionChange(selection: QuestionnairePickerSelection) {
    this.selectedItems = selection.selectedItems;
    this.canManageOneClickQRESets = selection.canManageOneClickQRESets;
    this.saveOneClickQRESetDisabled = selection.saveOneClickQRESetDisabled;
    this.refreshSendState();
  }

  public deselect(entry: PickerEntry) {
    if (this.picker) {
      this.picker.deselect(entry);
    }
  }

  public clearAllSelections() {
    if (this.picker) {
      this.picker.clearAllSelections();
    }
  }

  public saveOneClickQRESet() {
    if (this.picker) {
      this.picker.saveOneClickQRESet();
    }
  }

  public trackByEntryKey(_index: number, entry: PickerEntry): string {
    return entry.key;
  }

  public trackByPatientId(_index: number, patient: Patient): string {
    return patient.id;
  }

  public trackByRowPatientId(_index: number, row: RecipientRow): string {
    return row.patient.id;
  }

  public trackByLanguageId(_index: number, language: { id: string }): string {
    return language.id;
  }

  public goToRecipients() {
    if (this.selectedItems.length === 0) {
      return;
    }
    this.step = 'recipients';
    this.refreshSteps();
  }

  public goToSelection() {
    this.step = 'select';
    this.refreshSteps();
  }

  /* --- Step 2: who receives it ------------------------------------------- */

  private isDeliveryChannelEnabled(method: QuestionnaireDeliveryMethod): boolean {
    const config = this.subscriptionPlanAccessService.flattenedSubscriptionPlanSchema[
      `questionnaireDeliveryMethods.${method}`
    ];
    if (!config) {
      return true;
    }
    return config.accessState === AccessState.ENABLED;
  }

  public hasContactInformation(patient: Patient): boolean {
    return !!(patient && (patient.email || patient.phone));
  }

  public onPatientSelect(patient: Patient) {
    if (!patient) {
      return;
    }
    this.addPatient(patient);
  }

  public createNewPatient(patientFullName: string) {
    let [firstName, lastName] = patientFullName.split(/\s+/);
    firstName = capitalize(firstName);
    lastName = capitalize(lastName);
    this.massClinicService
      .openPatientModal({ firstName, lastName })
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe(newPatient => {
        if (newPatient) {
          this.addPatient(newPatient);
        }
      });
  }

  /** Routes a patient to the recipients table or the "needs contact information" list. */
  private addPatient(patient: Patient, position: 'start' | 'end' = 'start') {
    this.alreadyExists = false;
    if (this.patientAlreadyAdded(patient)) {
      this.alreadyExists = true;
      return;
    }
    if (!this.hasContactInformation(patient)) {
      this.setPatientsNeedingContactInformation([
        ...this.patientsNeedingContactInformation,
        patient
      ]);
      return;
    }
    this.addRecipientRow(this.toRecipientRow(patient), position);
  }

  /**
   * A row added during the session goes to the top and the paginator returns to page 1, so the add
   * is visible instead of landing on a page the user isn't looking at.
   */
  private addRecipientRow(row: RecipientRow, position: 'start' | 'end') {
    const index = position === 'start' ? 0 : this.recipientRows.length;
    this.recipientRows =
      position === 'start' ? [row, ...this.recipientRows] : [...this.recipientRows, row];
    this.sendMethods.insertAt(index, row);
    this.dataSource.data = this.recipientRows;
    if (position === 'start' && this.paginator) {
      this.paginator.firstPage();
    }
    this.updateMasterCheckboxes('email');
    this.updateMasterCheckboxes('phone');
    this.refreshSendState();
  }

  private patientAlreadyAdded(patient: Patient): boolean {
    return this.recipientRows
      .map(row => row.patient)
      .concat(this.patientsNeedingContactInformation)
      .some(addedPatient => addedPatient === patient || addedPatient.id === patient.id);
  }

  /** Opens the patient record so staff can fill in the missing email/phone without leaving. */
  public editContactInformation(patient: Patient) {
    this.massClinicService
      .openPatientModal(patient)
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe(editedPatient => {
        if (!editedPatient || !this.hasContactInformation(editedPatient)) {
          return;
        }
        this.removePatientNeedingContactInformation(patient);
        this.addRecipientRow(this.toRecipientRow(editedPatient), 'start');
      });
  }

  /**
   * The row is already in the table, so the missing address is filled in place rather than the
   * patient being pulled out and re-added: the row keeps its position in the list.
   */
  public addContactInformationToRow(row: RecipientRow) {
    this.massClinicService
      .openPatientModal(row.patient)
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe(editedPatient => {
        if (!editedPatient) {
          return;
        }
        this.replaceRecipientRow(row, editedPatient);
      });
  }

  /**
   * A gained email or phone has to come back as a fresh row: the warning flags and the enabled
   * state of the channel controls are both resolved when the row is built. Channels the patient
   * already had keep whatever the user had set them to — only the new one takes its default.
   */
  private replaceRecipientRow(row: RecipientRow, editedPatient: Patient) {
    const index = this.recipientRows.indexOf(row);
    if (index === -1) {
      return;
    }
    const updatedRow = this.toRecipientRow(editedPatient);
    if (row.patient.email && updatedRow.byEmail.enabled) {
      updatedRow.byEmail.setValue(row.byEmail.value, { emitEvent: false });
    }
    if (row.patient.phone && updatedRow.byPhone.enabled) {
      updatedRow.byPhone.setValue(row.byPhone.value, { emitEvent: false });
    }
    this.recipientRows = this.recipientRows.map(recipientRow =>
      recipientRow === row ? updatedRow : recipientRow
    );
    this.sendMethods.removeAt(index);
    this.sendMethods.insertAt(index, updatedRow);
    this.dataSource.data = this.recipientRows;
    this.updateMasterCheckboxes('email');
    this.updateMasterCheckboxes('phone');
    this.refreshSendState();
  }

  /**
   * Takes the row rather than a table index: under the paginator the template's index is relative
   * to the rendered page, so page 2's first × would have removed the first patient.
   */
  public removePatient(row: RecipientRow) {
    const index = this.recipientRows.indexOf(row);
    if (index === -1) {
      return;
    }
    this.recipientRows = this.recipientRows.filter(recipientRow => recipientRow !== row);
    this.sendMethods.removeAt(index);
    this.dataSource.data = this.recipientRows;
    this.clampPage();
    // Losing a row changes the all/some/none answer for both headers.
    this.updateMasterCheckboxes('email');
    this.updateMasterCheckboxes('phone');
    this.refreshSendState();
  }

  /** Removing the last row of the last page would otherwise leave the table showing nothing. */
  private clampPage() {
    if (!this.paginator || this.paginator.pageIndex === 0) {
      return;
    }
    if (this.paginator.pageIndex * this.paginator.pageSize >= this.recipientRows.length) {
      this.paginator.previousPage();
    }
  }

  public removePatientNeedingContactInformation(patient: Patient) {
    this.setPatientsNeedingContactInformation(
      this.patientsNeedingContactInformation.filter(pendingPatient => pendingPatient !== patient)
    );
  }

  /**
   * The count label is precomputed here rather than derived in the template: the list is mutated
   * from three places and a template getter would re-translate on every change-detection pass.
   */
  private setPatientsNeedingContactInformation(patients: Patient[]) {
    this.patientsNeedingContactInformation = patients;
    this.patientsNeedingContactLabel = this.translocoService.translate('{{count}} patient(s)', {
      count: patients.length
    });
  }

  /**
   * Deliberately spans every recipient, not just the rendered page: the header checkbox means "all
   * patients", and the Send button's count is the feedback that off-page rows were included too.
   */
  public setAllSendMethodsTo(change: MatCheckboxChange) {
    const isPhone = change.source.name === 'phone';
    const channelControls = isPhone
      ? this.sendMethods.controls.byPhone
      : this.sendMethods.controls.byEmail;
    // Skip the disabled rows: ticking the header must not arm a channel the patient cannot receive.
    channelControls.controls
      .filter(control => control.enabled)
      .forEach(control => control.setValue(change.checked));
    this.updateMasterCheckboxes(isPhone ? 'phone' : 'email');
  }

  public updateMasterCheckboxes(checkboxType: 'phone' | 'email') {
    const isPhone = checkboxType === 'phone';
    const channelControls = isPhone
      ? this.sendMethods.controls.byPhone
      : this.sendMethods.controls.byEmail;
    const checkbox = isPhone ? this.sendToPhoneControl : this.sendToEmailControl;
    // Rows that cannot use the channel are not part of the all/some/none question at all, and the
    // question is asked of every recipient rather than only the page on screen.
    const usableValues = channelControls.controls
      .filter(control => control.enabled)
      .map(control => control.value === true);
    const isIndeterminate =
      usableValues.some(value => value) && usableValues.some(value => !value);

    if (isPhone) {
      this.sendToPhoneIndeterminate = isIndeterminate;
    } else {
      this.sendToEmailIndeterminate = isIndeterminate;
    }

    if (usableValues.length > 0 && usableValues.every(value => value)) {
      checkbox.setValue(true);
    } else if (usableValues.every(value => !value)) {
      checkbox.setValue(false);
    }
  }

  /* --- Send --------------------------------------------------------------- */

  /**
   * One request per recipient per selected questionnaire, skipping rows with no live channel. Walks
   * every recipient, so which page is on screen never changes what gets sent.
   */
  public buildPreparedSends(): PreparedQuestionnaireSend[] {
    const selectedKeys = this.selectedItems.map(entry => entry.key);
    const preparedSends: PreparedQuestionnaireSend[] = [];

    this.recipientRows.forEach(row => {
      const patient = row.patient;
      const { sendToEmail, sendToPhone } = this.channelsFor(row);
      if (!sendToEmail && !sendToPhone) {
        return;
      }
      // One group per patient, so a patient receiving several questionnaires gets them as a
      // single flow exactly as they would from the single-patient dialog.
      const groupId = selectedKeys.length > 1 ? uuid() : undefined;
      selectedKeys.forEach(questionnaireKey => {
        preparedSends.push({
          input: this.questionnaireSendBuilderService.buildQuestionnaireRequestInput(
            patient,
            questionnaireKey,
            {
              sendToEmail,
              sendToPhone,
              language: this.languageFormControl.value,
              groupId
            }
          ),
          displayName: this.describeSend(patient, questionnaireKey)
        });
      });
    });

    return preparedSends;
  }

  private describeSend(patient: Patient, questionnaireKey: string): string {
    const patientName = [patient.firstName, patient.lastName]
      .filter(namePart => !!namePart)
      .join(' ');
    const questionnaireName = this.selectedItems
      .filter(entry => entry.key === questionnaireKey)
      .map(entry => entry.name)[0];
    return `${patientName || 'Unnamed patient'} (${questionnaireName || questionnaireKey})`;
  }

  /**
   * Names what is going out rather than counting requests: "54 questionnaire(s)" told staff
   * nothing about what the 18 patients would actually receive. The names are chips on a wrapping
   * row, so the modal keeps its shape whether one or twenty are selected.
   *
   * The confirm modal renders its body through [innerHTML]. That is why the chips carry global
   * `csi-chip` classes — component styles never reach innerHTML content — and why the names are
   * escaped: a questionnaire name can come from a clinic-authored form template.
   */
  private buildConfirmationBody(): string {
    const sentence = this.translocoService.translate(
      'The following questionnaires will be sent to {{count}} patient(s).',
      { count: `<strong>${this.reachableRecipientCount}</strong>` }
    );
    const chips = this.selectedItems
      .map(
        entry =>
          `<span class="csi-chip csi-chip--teal">${escapeHtml(
            this.translocoService.translate(entry.name)
          )}</span>`
      )
      .join('');
    return `${sentence}<span class="csi-chip-list">${chips}</span>`;
  }

  public sendQuestionnaires() {
    if (this.sendDisabled) {
      return;
    }
    const preparedSends = this.buildPreparedSends();
    if (preparedSends.length === 0) {
      this.snackBar.open(
        this.translocoService.translate(
          'No patient has a delivery method selected. Pick email or text for at least one patient.'
        ),
        '',
        { duration: 4000 }
      );
      return;
    }

    this.confirmModalService
      .show(
        this.translocoService.translate('Send Questionnaires'),
        this.buildConfirmationBody(),
        this.translocoService.translate('Send'),
        this.translocoService.translate('Cancel')
      )
      .pipe(untilDestroyed(this))
      .subscribe(confirmed => {
        if (!confirmed) {
          return;
        }
        this.sending = true;
        this.refreshSendState();
        this.importWizardSendQuestionnairesService
          .sendPreparedRequests(preparedSends)
          .pipe(untilDestroyed(this))
          .subscribe(
            result => this.onSendComplete(result),
            () => {
              this.sending = false;
              this.refreshSendState();
              this.snackBar.open(
                this.translocoService.translate('Could not send the questionnaires.'),
                '',
                { duration: 4000 }
              );
            }
          );
      });
  }

  /** Reports partial failures instead of claiming blanket success. */
  private onSendComplete(result: SendQuestionnairesResult) {
    this.sending = false;
    this.refreshSendState();
    this.completed.emit(null);

    if (result.failures.length === 0) {
      this.snackBar.open(
        this.translocoService.translate('Questionnaire(s) sent successfully!'),
        '',
        { duration: 4000, verticalPosition: 'bottom' }
      );
      this.dialogRef.close(result);
      return;
    }

    this.confirmModalService
      .show(
        this.translocoService.translate('Some questionnaires were not sent'),
        `${result.sent.length} ${this.translocoService.translate(
          'sent'
        )}. ${this.translocoService.translate('Not Sent')}: ${result.failures
          .map(failure => `${failure.name} — ${failure.reason}`)
          .join('; ')}`,
        this.translocoService.translate('Close'),
        ''
      )
      .pipe(untilDestroyed(this))
      .subscribe(() => this.dialogRef.close(result));
  }
}
